/**
 * Widget configuration panel for the personal dashboard.
 * Shows configuration options for each widget type
 */

import { X } from '@phosphor-icons/react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Button } from '@/components/ui/button'
import { WORKFLOW_MANIFESTS } from '../mock-multi-workflow-data'
import type { WidgetType, DashboardScope } from '../types'

interface WidgetConfigPanelProps {
  widgetType: WidgetType
  widgetTitle?: string
  config: any
  usesCustomScope: boolean
  customScope?: Partial<DashboardScope>
  onConfigChange: (key: string, value: any) => void
  onToggleCustomScope: () => void
  onCustomScopeChange: (scope: Partial<DashboardScope>) => void
  onClose?: () => void
}

export function WidgetConfigPanel({
  widgetType,
  widgetTitle,
  config,
  usesCustomScope,
  customScope,
  onConfigChange,
  onToggleCustomScope,
  onCustomScopeChange,
  onClose,
}: WidgetConfigPanelProps) {
  return (
    <div className="space-y-4 rounded-xl border border-[#e6e6e6] bg-white p-4 shadow-sm">
      <div className="flex items-center justify-between border-b border-[#e6e6e6] pb-3">
        <div className="text-sm font-medium tracking-[0.14px] text-[#1d1d1d]">
          {widgetTitle ? `Configure: ${widgetTitle}` : 'Widget Configuration'}
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="rounded-lg p-1 text-[#727272] transition-colors hover:bg-[#f5f5f5] hover:text-[#272727]"
            aria-label="Close widget configuration"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Custom Scope Toggle */}
      <div className="space-y-3 rounded-xl border border-[#e6e6e6] bg-[#fafafa] p-3">
        <div className="flex items-center justify-between">
          <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Scope Behavior</Label>
          <Switch
            checked={usesCustomScope}
            onCheckedChange={onToggleCustomScope}
          />
        </div>
        <p className="text-xs tracking-[0.12px] text-[#727272]">
          {usesCustomScope
            ? 'Using custom scope (widget has its own filters)'
            : 'Using dashboard scope (inherits from dashboard)'}
        </p>

        {/* Custom Scope Filters */}
        {usesCustomScope && (
          <div className="mt-3 space-y-3 rounded-xl border border-[#ff8637] bg-[#fff7ed] p-3">
            <div className="text-xs font-bold tracking-[0.12px] text-[#c83408]">Custom Workflow Filter</div>
            <div className="flex flex-wrap gap-2">
              {WORKFLOW_MANIFESTS.map((wf) => (
                <button
                  key={wf.workflowId}
                  onClick={() => {
                    const current = customScope?.workflows || []
                    const newWorkflows = current.includes(wf.workflowName)
                      ? current.filter((w) => w !== wf.workflowName)
                      : [...current, wf.workflowName]
                    onCustomScopeChange({ ...customScope, workflows: newWorkflows })
                  }}
                  className={`rounded-3xl px-2 py-1 text-[10px] font-bold leading-[15px] tracking-[0.1px] transition-all ${
                    customScope?.workflows?.includes(wf.workflowName)
                      ? wf.isCustomerBuilt
                        ? 'bg-[#e9f9ff] text-[#0065b3]'
                        : 'bg-[#1d1d1d] text-[#fafafa]'
                      : 'border border-[#e6e6e6] bg-white text-[#727272] hover:border-[#ff5c04]'
                  }`}
                >
                  {wf.workflowName}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Widget-Specific Options */}
      {widgetType === 'severity_distribution' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Chart Type</Label>
            <Select
              value={config.chartType || 'donut'}
              onValueChange={(val) => onConfigChange('chartType', val)}
            >
              <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="donut">Donut Chart</SelectItem>
                <SelectItem value="pie">Pie Chart</SelectItem>
                <SelectItem value="bar">Bar Chart</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Show Legend</Label>
            <Switch
              checked={config.showLegend !== false}
              onCheckedChange={(val) => onConfigChange('showLegend', val)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Show Percentages</Label>
            <Switch
              checked={config.showPercentages !== false}
              onCheckedChange={(val) => onConfigChange('showPercentages', val)}
            />
          </div>
        </div>
      )}

      {widgetType === 'impact_by_product' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Sort By</Label>
            <Select
              value={config.sortBy || 'wafers'}
              onValueChange={(val) => onConfigChange('sortBy', val)}
            >
              <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="wafers">Wafers Impacted (Highest)</SelectItem>
                <SelectItem value="lots">Lots Impacted (Highest)</SelectItem>
                <SelectItem value="product">Product Name (A-Z)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Max Products Shown</Label>
            <Select
              value={String(config.limit || 7)}
              onValueChange={(val) => onConfigChange('limit', parseInt(val))}
            >
              <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">Top 5</SelectItem>
                <SelectItem value="7">Top 7</SelectItem>
                <SelectItem value="10">Top 10</SelectItem>
                <SelectItem value="15">All Products</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Show Legend</Label>
            <Switch
              checked={config.showLegend !== false}
              onCheckedChange={(val) => onConfigChange('showLegend', val)}
            />
          </div>
        </div>
      )}

      {widgetType === 'excursion_table' && (
        <div className="space-y-3">
          <div className="space-y-2">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Rows per Page</Label>
            <Select
              value={String(config.rowsPerPage || 10)}
              onValueChange={(val) => onConfigChange('rowsPerPage', parseInt(val))}
            >
              <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="5">5 rows</SelectItem>
                <SelectItem value="10">10 rows</SelectItem>
                <SelectItem value="25">25 rows</SelectItem>
                <SelectItem value="50">50 rows</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Default Sort</Label>
            <Select
              value={config.sortBy || 'date'}
              onValueChange={(val) => onConfigChange('sortBy', val)}
            >
              <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="date">Date (Newest First)</SelectItem>
                <SelectItem value="severity">Severity (Highest First)</SelectItem>
                <SelectItem value="product">Product Name</SelectItem>
                <SelectItem value="lots">Lots Impacted</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Enable Search</Label>
            <Switch
              checked={config.enableSearch !== false}
              onCheckedChange={(val) => onConfigChange('enableSearch', val)}
            />
          </div>

          <div className="flex items-center justify-between">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Show Column Selector</Label>
            <Switch
              checked={config.showColumns !== false}
              onCheckedChange={(val) => onConfigChange('showColumns', val)}
            />
          </div>
        </div>
      )}

      {/* Multi-workflow widgets */}
      {(widgetType === 'open_action_queue' ||
        widgetType === 'insights_needing_attention' ||
        widgetType === 'impacted_business_objects') && (
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Show Workflow Source</Label>
            <Switch
              checked={config.showWorkflowSource !== false}
              onCheckedChange={(val) => onConfigChange('showWorkflowSource', val)}
            />
          </div>

          {widgetType !== 'impacted_business_objects' && (
            <div className="space-y-2">
              <Label className="text-sm tracking-[0.14px] text-[#1d1d1d]">Max Items</Label>
              <Select
                value={String(config.limit || 10)}
                onValueChange={(val) => onConfigChange('limit', parseInt(val))}
              >
                <SelectTrigger className="h-10 rounded-xl border-[#e6e6e6]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5">5 items</SelectItem>
                  <SelectItem value="10">10 items</SelectItem>
                  <SelectItem value="15">15 items</SelectItem>
                  <SelectItem value="20">20 items</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
